/**
 * Convert Quill `<p>` blocks to `<br>`-separated inline content, mirroring the
 * operator's typed Enters 1:1. Byte-parity with the Python port
 * `server/emailbison/rendering/render.py:flatten_paragraphs_to_breaks`.
 *
 * Quill emits one `<p>` per line; rendered as real paragraphs, every line
 * picks up paragraph margins (a blank line) in email clients and in the
 * preview's `prose` styling, so an HTML campaign looks double-spaced relative
 * to the plain-text rendering ("one Enter = one line"). Flattening to `<br>`
 * keeps the two in sync while preserving inline formatting (bold, italics,
 * anchors, lists) inside each paragraph — only the paragraph tags are rewritten.
 *
 * Counting model: one Enter in the editor = one `<br>` in the output. Quill
 * represents an empty paragraph (Enter on a blank line) as `<p><br></p>`; the
 * structural inner `<br>` is stripped first so the surrounding paragraph
 * boundary contributes the only break:
 *
 *   <p>A</p><p>B</p>                       -> A<br>B           (1 Enter)
 *   <p>A</p><p><br></p><p>B</p>            -> A<br><br>B       (2 Enters)
 *   <p>A</p><p><br></p><p><br></p><p>B</p> -> A<br><br><br>B   (3 Enters)
 */
const QUILL_EMPTY_P_RE = /<p[^>]*>\s*<br\s*\/?>\s*<\/p>/gi
const TRAILING_BR_RE = /(?:<br\s*\/?>\s*)+$/i

export function flattenParagraphsToBreaks(html: string): string {
  return (
    html
      // Drop the structural <br> inside `<p><br></p>` so the closing </p>
      // contributes the only break for that paragraph.
      .replace(QUILL_EMPTY_P_RE, '<p></p>')
      // Every `<p ...>` open is stripped; every `</p>` becomes one `<br>`.
      .replace(/<p[^>]*>/gi, '')
      .replace(/<\/p>/gi, '<br>')
      // The final paragraph adds a trailing `<br>` that nothing follows;
      // strip it (and any whitespace) so the body ends on its last word.
      .replace(TRAILING_BR_RE, '')
      .replace(/\s+$/, '')
  )
}
